import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { InternalDeclineReason } from './internal-decline-reasons.interface';
import { InternalDeclineReasonsService } from './internal-decline-reasons.service';
import { InternalDeclineReasonsReferralService } from '../../manageb/internal-decline-reasons-referral.service';
import { UiInputComponent } from '../shared/ui/ui-input/ui-input.component';
import { UtilsService } from '../../manageb/utils.service';
import { ClientSettingsService } from '../../manageb/client-settings.service';

// Shared by the local check and the 409 arm so they cannot drift. Capitalization is spelled
// out because the colliding name may be on screen in another case, or not on screen at all.
const DUPLICATE_NAME_ERROR = 'A reason with this name already exists in this category. Names ignore capitalization.';

// The action landed; only this list did not follow. Says so, rather than casting doubt
// on the change itself, and names the reload that fixes it.
const REFRESH_FAILED_MESSAGE =
  'That change was saved, but this list could not be refreshed and may be out of date. Reload the page to see the current reasons.';

/** A category (parentId null) with the reasons filed under it. Rebuilt from `reasons` on every change. */
interface CategoryGroup {
  category: InternalDeclineReason;
  reasons: InternalDeclineReason[];
  expanded: boolean;
}

@Component({
  selector: 'internal-decline-reasons',
  templateUrl: './internal-decline-reasons.component.html',
  styleUrls: ['./internal-decline-reasons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalDeclineReasonsComponent implements OnInit, OnDestroy {
  loading = true;
  loadError: string | null = null;
  actionError: string | null = null;
  actionMessage: string | null = null;
  /** Set when the refresh after a successful action failed: the action landed, this list did not follow. */
  refreshError: string | null = null;

  /** Every category and reason in the region, flat — categoryGroups is derived from this. */
  reasons: InternalDeclineReason[] = [];
  categoryGroups: CategoryGroup[] = [];

  featureEnabled = false;
  featureSaving = false;
  featureToggleError: string | null = null;

  creating = false;
  addingCategory = false;
  newCategoryName = '';

  /** The category a reason is being added under, or null when no such row is open. */
  addingReasonToCategoryId: string | null = null;
  newReasonName = '';

  editingReasonId: string | null = null;
  editDraftName = '';
  renamingReasonId: string | null = null;

  deleteTargetReason: InternalDeclineReason | null = null;
  /** Mirrors `deleteTargetReason.reasonId` so rows compare a field instead of chaining in the template. */
  deleteTargetReasonId: string | null = null;
  deleteTargetInUse = false;
  deleteConfirmMessage = '';
  deletingReasonId: string | null = null;

  /** Named by template ref: a bare type query would match the rename editor's input first. */
  @ViewChild('newCategoryInput') private newCategoryInput?: UiInputComponent;
  @ViewChild('newReasonInput') private newReasonInput?: UiInputComponent;

  private readonly destroy$ = new Subject<void>();
  private settingsSubscription?: Subscription;
  /** Every post-action refresh is requested here. Drained by one stream — see ngOnInit. */
  private readonly refreshRequests$ = new Subject<void>();

  constructor(
    private internalDeclineReasonsService: InternalDeclineReasonsService,
    private referralReasonsCache: InternalDeclineReasonsReferralService,
    private utils: UtilsService,
    private clientSettingsService: ClientSettingsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // settings$ is a BehaviorSubject, so this fires immediately with whatever is
    // cached now (often still the pre-load defaults on a fresh page open) and again
    // once the real getClientSettings response lands — a one-shot read here would
    // freeze the toggle on "off" until a full reload if this page opens before that
    // fetch resolves. distinctUntilChanged keeps this quiet for unrelated uiSettings
    // patches (smsEnabled, aiDecisionLogicEnabled, ...); the featureSaving guard below
    // stops an unrelated emission from stomping the optimistic flip in toggleFeature().
    this.settingsSubscription = this.clientSettingsService.settings$
      .pipe(
        map(() => this.utils.isInternalDeclineReasonsEnabled()),
        distinctUntilChanged(),
      )
      .subscribe((enabled) => {
        if (!this.featureSaving) {
          this.featureEnabled = enabled;
          this.cdr.markForCheck();
        }
      });

    // One stream drains every refresh request, so only the newest one can write to the
    // list. switchMap, not mergeMap or concatMap: a refresh started by a later action
    // supersedes one still in flight, so a slow earlier response can neither restore an
    // obsolete tree over the newer one nor clear a newer staleness warning.
    this.refreshRequests$
      .pipe(
        switchMap(() =>
          this.internalDeclineReasonsService.getReasons().pipe(
            map((response) => response?.reasons ?? []),
            // Caught on the inner request rather than the outer stream: an error allowed
            // to reach the outer pipe would complete it, and every later action would
            // silently stop refreshing. null is the failure — map above never returns it.
            catchError(() => of<InternalDeclineReason[] | null>(null)),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((refreshedReasons) => {
        if (refreshedReasons === null) {
          this.refreshError = REFRESH_FAILED_MESSAGE;
        } else {
          this.setReasons(refreshedReasons);
          this.refreshError = null;
        }
        this.cdr.markForCheck();
      });

    this.loadReasons();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refreshRequests$.complete();
    this.settingsSubscription?.unsubscribe();
  }

  toggleFeature(): void {
    if (this.featureSaving) {
      return;
    }
    const next = !this.featureEnabled;
    this.featureEnabled = next;
    this.featureSaving = true;
    this.featureToggleError = null;
    this.cdr.markForCheck();

    this.internalDeclineReasonsService
      .setEnabled(next)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Patch through the service so `settings$` emits — assigning to
          // `clientSettings.uiSettings` directly would mutate the cached object
          // in place and notify nobody (see UtilsService.patchUiSettings).
          this.utils.patchUiSettings({ internalDeclineReasonsEnabled: next });
          this.featureSaving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.featureEnabled = !next;
          this.featureSaving = false;
          this.featureToggleError = 'Failed to save internal decline reasons setting.';
          this.cdr.markForCheck();
        },
      });
  }

  toggleCategory(categoryId: string): void {
    this.categoryGroups = this.categoryGroups.map((group) =>
      group.category.reasonId === categoryId ? { ...group, expanded: !group.expanded } : group,
    );
  }

  startAddCategory(): void {
    this.addingCategory = true;
    this.addingReasonToCategoryId = null;
    this.editingReasonId = null;
    this.newCategoryName = '';
    this.clearActionMessages();
    // The field sits behind *ngIf, so it is not rendered until this change lands.
    setTimeout(() => this.newCategoryInput?.focus(), 0);
  }

  cancelAddCategory(): void {
    this.addingCategory = false;
    this.newCategoryName = '';
  }

  createCategory(): void {
    this.performCreate(this.newCategoryName, null, () => {
      this.newCategoryName = '';
      this.addingCategory = false;
    });
  }

  startAddReasonToCategory(categoryId: string): void {
    this.addingReasonToCategoryId = categoryId;
    this.addingCategory = false;
    this.editingReasonId = null;
    this.newReasonName = '';
    this.clearActionMessages();
    // Expand the category so the new row is visible once it lands.
    this.categoryGroups = this.categoryGroups.map((group) =>
      group.category.reasonId === categoryId ? { ...group, expanded: true } : group,
    );
    setTimeout(() => this.newReasonInput?.focus(), 0);
  }

  cancelAddReason(): void {
    this.addingReasonToCategoryId = null;
    this.newReasonName = '';
  }

  createReasonInCategory(categoryId: string): void {
    this.performCreate(this.newReasonName, categoryId, () => {
      this.newReasonName = '';
      this.addingReasonToCategoryId = null;
    });
  }

  private performCreate(name: string, parentId: string | null, onCreated: () => void): void {
    const normalizedName = this.normalizeReasonName(name);
    if (!normalizedName) {
      this.setActionError('Name is required.');
      return;
    }

    if (this.hasDuplicateName(normalizedName, parentId)) {
      this.setActionError(DUPLICATE_NAME_ERROR);
      return;
    }

    this.creating = true;
    this.clearActionMessages();

    this.internalDeclineReasonsService
      .createReason({ name: normalizedName, parentId: parentId ?? undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createResponse) => {
          this.referralReasonsCache.resetCache();
          this.creating = false;
          // `defaultReason` is the server saying it filed one, which only a category
          // create gets — not this component inferring it from `parentId`.
          this.actionMessage = createResponse.defaultReason
            ? `Added "${createResponse.name}" with a default reason of the same name`
            : `Added "${createResponse.name}"`;
          onCreated();
          this.refreshReasons();
          this.cdr.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          this.creating = false;
          this.setActionError(
            error.status === 409 ? DUPLICATE_NAME_ERROR : 'Could not create.',
          );
          this.cdr.markForCheck();
        },
      });
  }

  loadReasons(): void {
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.internalDeclineReasonsService.getReasons().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.setReasons(response?.reasons ?? []);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError = 'Could not load internal decline reasons.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Pulls the list again after a successful action instead of reproducing what the
   * server did to it. Creating a category also files a default reason under it, deleting
   * cascades in both directions, and another admin may have changed either since this
   * page loaded — rules this component would have to mirror exactly, forever, to keep
   * the tree honest.
   *
   * Quiet on purpose: no spinner and no `loadError`, because both would replace the
   * action's own feedback with either a flash of the loading state or a page-level load
   * error for something that did succeed. A refresh that fails leaves the list already
   * on screen and says it may be stale.
   *
   * A request, not a subscription: the single stream in ngOnInit owns the fetch, which
   * is what keeps two actions completing close together from landing out of order.
   */
  private refreshReasons(): void {
    this.refreshRequests$.next();
  }

  startRename(reason: InternalDeclineReason): void {
    this.clearActionMessages();
    if (!this.isRowAddressable(reason)) {
      return;
    }
    this.addingCategory = false;
    this.addingReasonToCategoryId = null;
    this.editingReasonId = reason.reasonId;
    this.editDraftName = reason.name;
  }

  cancelRename(): void {
    this.editingReasonId = null;
    this.editDraftName = '';
  }

  saveRename(reason: InternalDeclineReason): void {
    const normalizedName = this.normalizeReasonName(this.editDraftName);
    if (!normalizedName) {
      this.setActionError('Name is required.');
      return;
    }

    if (normalizedName === reason.name) {
      this.cancelRename();
      return;
    }

    if (this.hasDuplicateName(normalizedName, reason.parentId, reason.reasonId)) {
      this.setActionError(DUPLICATE_NAME_ERROR);
      return;
    }

    this.renamingReasonId = reason.reasonId;
    this.clearActionMessages();

    this.internalDeclineReasonsService
      .renameReason(reason.reasonId, { name: normalizedName })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.referralReasonsCache.resetCache();
          this.cancelRename();
          this.renamingReasonId = null;
          this.actionMessage = `Renamed to "${normalizedName}"`;
          this.refreshReasons();
          this.cdr.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          this.renamingReasonId = null;
          this.setActionError(
            error.status === 409 ? DUPLICATE_NAME_ERROR : 'Could not rename.',
          );
          this.cdr.markForCheck();
        },
      });
  }

  openDeleteDialog(reason: InternalDeclineReason): void {
    this.clearActionMessages();
    if (!this.isRowAddressable(reason)) {
      return;
    }
    this.editingReasonId = null;
    this.deleteTargetReason = reason;
    this.deleteTargetReasonId = reason.reasonId;
    const childReasons = this.childrenOf(reason);
    const childUsageCount = childReasons.reduce((total, child) => total + (child.usageCount ?? 0), 0);
    this.deleteTargetInUse = reason.usageCount + childUsageCount > 0;
    this.deleteConfirmMessage = this.buildDeleteConfirmMessage(reason, childReasons.length, childUsageCount);
  }

  closeDeleteDialog(): void {
    if (this.deletingReasonId !== null) {
      return;
    }
    this.deleteTargetReason = null;
    this.deleteTargetReasonId = null;
    this.deleteTargetInUse = false;
    this.deleteConfirmMessage = '';
  }

  renameInstead(): void {
    if (!this.deleteTargetReason) {
      return;
    }
    const targetReason = this.deleteTargetReason;
    this.closeDeleteDialog();
    this.startRename(targetReason);
  }

  confirmDelete(): void {
    if (!this.deleteTargetReason) {
      return;
    }

    const reasonIdToDelete = this.deleteTargetReason.reasonId;
    const deletedReasonName = this.deleteTargetReason.name;
    const usageCountBeforeDelete = this.deleteTargetReason.usageCount;
    const childCount = this.childCountFor(this.deleteTargetReason);
    this.deletingReasonId = reasonIdToDelete;
    this.clearActionMessages();

    this.internalDeclineReasonsService
      .deleteReason(reasonIdToDelete)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deleteResponse) => {
          this.referralReasonsCache.resetCache();
          // The cases the retired reason stays on. The server counts them after
          // the retirement; the pre-delete count is the fallback, and it is the same
          // number either way because retiring writes nothing to the cases.
          const keptCasesCount = deleteResponse?.usageCount ?? usageCountBeforeDelete;
          // The server reports the category it retired alongside a last reason, rather
          // than this list guessing: another admin may have added a sibling since load.
          // Named off the list as it still stands, before the refresh replaces it.
          const deletedCategoryId = deleteResponse?.deletedCategoryId ?? null;
          const deletedCategoryName = deletedCategoryId
            ? this.reasons.find((reason) => reason.reasonId === deletedCategoryId)?.name
            : undefined;
          this.deletingReasonId = null;
          this.closeDeleteDialog();
          const keptPart = keptCasesCount > 0 ? ` · kept on ${this.pluralize(keptCasesCount, 'existing case')}` : '';
          const cascadePart = childCount > 0 ? ` · retired ${this.pluralize(childCount, 'reason')} with it` : '';
          const categoryPart = deletedCategoryId
            ? ` · deleted its category${deletedCategoryName ? ` "${deletedCategoryName}"` : ''} too`
            : '';
          this.actionMessage = `Deleted "${deletedReasonName}"${keptPart}${cascadePart}${categoryPart}`;
          this.refreshReasons();
          this.cdr.markForCheck();
        },
        error: () => {
          this.deletingReasonId = null;
          this.setActionError('Could not delete.');
          this.cdr.markForCheck();
        },
      });
  }

  trackByReasonId(_: number, reason: InternalDeclineReason): string {
    return reason.reasonId;
  }

  trackByCategoryId(_: number, group: CategoryGroup): string {
    return group.category.reasonId;
  }

  useCountLabel(count: number): string {
    if (count === 0) {
      return '—';
    }
    return this.pluralize(count, 'case');
  }

  // Reads `reasons`, not `categoryGroups` — stays correct even if only the flat list is set.
  private childrenOf(reason: InternalDeclineReason): InternalDeclineReason[] {
    if (reason.parentId != null) {
      return [];
    }
    return this.reasons.filter((candidate) => candidate.parentId === reason.reasonId);
  }

  private childCountFor(reason: InternalDeclineReason): number {
    return this.childrenOf(reason).length;
  }

  // Delete retires the row server-side: it leaves this list and the picker, but
  // cases already filed under it keep it and keep showing its name. So the
  // usage count is what the delete LEAVES BEHIND, not what it clears.
  //
  // Four sentences in reading order: what is being deleted, what stops being pickable,
  // what the delete drags along with it, and what it leaves on this row's own cases.
  private buildDeleteConfirmMessage(
    reason: InternalDeclineReason,
    childCount: number,
    childUsageCount: number,
  ): string {
    const kind = reason.parentId == null ? 'category' : 'reason';
    // Anything left behind — on this row or on the reasons under it — makes "no longer
    // available" a claim about new declines only, not about the cases already filed.
    const leavesCasesBehind = reason.usageCount + childUsageCount > 0;

    const lead = `Delete this ${kind}, "${reason.name}"?`;
    const availability = leavesCasesBehind
      ? 'It will no longer be available to pick on new declines.'
      : 'It will no longer be available to pick.';
    const cascade = `${this.buildChildRetirementWarning(childCount, childUsageCount)}${this.buildCategoryDeletionWarning(reason)}`;
    const ownUsage = this.buildOwnUsageSentence(reason, kind, childUsageCount);

    return `${lead} ${availability}${cascade} ${ownUsage}`;
  }

  // The cases filed on this row itself. `usageCount` counts one row's own assignments and
  // rolls nothing up, so the cases under a category's reasons are not in it — those are
  // reported by buildChildRetirementWarning, next to the retirement that reaches them,
  // rather than added in here into one number no screen can be reconciled against.
  private buildOwnUsageSentence(
    reason: InternalDeclineReason,
    kind: 'category' | 'reason',
    childUsageCount: number,
  ): string {
    if (reason.usageCount > 0) {
      const keeps = reason.usageCount === 1 ? 'that case keeps it' : 'those cases keep it';
      return `It is already used on ${this.pluralize(reason.usageCount, 'case')} and ${keeps} — rename it instead if you want the wording changed there.`;
    }
    // A bare "It is not used on any cases" would read as the delete reaching none, which
    // the cascade sentence has just contradicted when the reasons under it carry cases.
    return childUsageCount > 0
      ? `The ${kind} itself is not used on any cases.`
      : 'It is not used on any cases.';
  }

  // Deleting a category retires every reason under it, and those cases survive the
  // cascade exactly as a directly retired reason's do.
  private buildChildRetirementWarning(childCount: number, childUsageCount: number): string {
    if (childCount <= 0) {
      return '';
    }
    const reasonsLabel = this.pluralize(childCount, 'reason');
    if (childUsageCount <= 0) {
      return ` Its ${reasonsLabel} will be retired with it.`;
    }
    // "with it" already means the category here, so a lone child reason is named rather
    // than pronounced — three unqualified "it"s in one sentence read as one thing.
    const usingWhat = childCount === 1 ? 'that reason' : 'them';
    const keepWhat = childCount === 1 ? 'it' : 'them';
    const keep = childUsageCount === 1 ? 'keeps' : 'keep';
    const casesLabel = this.pluralize(childUsageCount, 'case');
    return ` Its ${reasonsLabel} will be retired with it, and the ${casesLabel} using ${usingWhat} ${keep} ${keepWhat}.`;
  }

  private pluralize(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
  }

  // Every category holds at least one reason, so the server takes the category with its
  // last one. Warning about it here is the only place the admin learns that before
  // confirming — see deleteAdminInternalDeclineReason (WorkflowServer).
  private buildCategoryDeletionWarning(reason: InternalDeclineReason): string {
    if (!this.isLastReasonInCategory(reason)) {
      return '';
    }
    const category = this.reasons.find((candidate) => candidate.reasonId === reason.parentId);
    const categoryLabel = category ? `"${category.name}"` : 'its category';
    return ` It is the only reason in ${categoryLabel}, so that category will be deleted too.`;
  }

  // `reasons` holds only active rows, so "no sibling here" is the same question the
  // server asks of the active reasons under this parent.
  private isLastReasonInCategory(reason: InternalDeclineReason): boolean {
    if (reason.parentId == null) {
      return false;
    }
    return !this.reasons.some(
      (candidate) => candidate.parentId === reason.parentId && candidate.reasonId !== reason.reasonId,
    );
  }

  private isRowAddressable(reason: InternalDeclineReason): boolean {
    if (reason?.reasonId) {
      return true;
    }
    this.setActionError('This row is missing its id and cannot be edited or deleted. Reload the page and try again.');
    return false;
  }

  // Mirrors the server: trim, then compare without case (its index carries a strength-2
  // collation), scoped to the same parentId group — a name can repeat once per category.
  private hasDuplicateName(name: string, parentId: string | null, currentReasonId?: string): boolean {
    const normalized = this.normalizeReasonName(name);
    return this.reasons.some((reason) => {
      if (currentReasonId !== undefined && reason.reasonId === currentReasonId) {
        return false;
      }
      if ((reason.parentId ?? null) !== (parentId ?? null)) {
        return false;
      }
      return this.isSameReasonName(reason.name, normalized);
    });
  }

  // `sensitivity: 'accent'` is the Intl equivalent of collation strength 2: folds case,
  // keeps accents. The server's rule expressed the same way, not one that merely agrees.
  private isSameReasonName(storedName: string, normalizedName: string): boolean {
    return storedName.trim().localeCompare(normalizedName, undefined, { sensitivity: 'accent' }) === 0;
  }

  private normalizeReasonName(value: string): string {
    return (value ?? '').trim();
  }

  /** The single seam where the server's list becomes the tree on screen. */
  private setReasons(reasons: InternalDeclineReason[]): void {
    this.reasons = this.sortReasons(reasons);
    this.categoryGroups = this.buildCategoryGroups(this.reasons);
  }

  private sortReasons(reasons: InternalDeclineReason[]): InternalDeclineReason[] {
    return [...reasons].sort((leftReason, rightReason) => leftReason.name.localeCompare(rightReason.name));
  }

  // Carries expand state across rebuilds; new categories default open.
  private buildCategoryGroups(reasons: InternalDeclineReason[]): CategoryGroup[] {
    const previouslyExpanded = new Map(this.categoryGroups.map((group) => [group.category.reasonId, group.expanded]));
    const categories = this.sortReasons(reasons.filter((reason) => reason.parentId == null));

    return categories.map((category) => ({
      category,
      reasons: this.sortReasons(reasons.filter((reason) => reason.parentId === category.reasonId)),
      expanded: previouslyExpanded.get(category.reasonId) ?? true,
    }));
  }

  private clearActionMessages(): void {
    this.actionError = null;
    this.actionMessage = null;
    this.refreshError = null;
  }

  private setActionError(message: string): void {
    this.actionError = message;
  }
}
